import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface PldRule {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  type: string;
  config: any;
  severity: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PldAlert {
  id: string;
  tenantId: string;
  ruleId: string;
  ruleName: string;
  ruleType: string;
  transactionId?: string;
  accountId: string;
  userId?: string;
  userName?: string;
  severity: string;
  status: string;
  resolution?: string;
  description?: string;
  details: any;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  escalatedAt?: string;
  coafCommunicationId?: string;
  createdAt: string;
  updatedAt: string;
  // Relacionamentos expandidos (quando incluídos na query)
  transaction?: {
    id: string;
    type: string;
    amount: number;
    currency: string;
    createdAt: string;
  };
  account?: {
    id: string;
    name: string;
    holderName?: string;
    document: string;
    type: string;
    balance: number;
    branch?: string;
    number?: string;
  };
  rule?: {
    id: string;
    name: string;
    type: string;
    severity: string;
    description?: string;
  };
  history?: {
    id: string;
    action: string;
    user?: { name: string };
    createdAt?: string;
    notes?: string;
  }[];
}

export interface PldConfig {
  id: string;
  tenantId: string;
  isEnabled: boolean;
  autoEscalationDays: number;
  autoEscalationHours?: number; // Alias para compatibilidade
  notifyOnHighSeverity: boolean;
  notifyOnCritical: boolean;
  notifyOnCriticalSeverity?: boolean; // Alias para compatibilidade
  blockOnCritical: boolean;
  dailyReportEnabled?: boolean;
  dailyReportTime?: string;
  retentionDays?: number;
  complianceEmail?: string;
  escalationEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PldDashboard {
  totalAlerts: number;
  pendingAlerts: number;
  reviewedAlerts: number;
  escalatedAlerts: number;
  alertsBySeverity: Record<string, number>;
  alertsByStatus: Record<string, number>;
  alertsByRuleType: Record<string, number>;
  recentAlerts: PldAlert[];
  trends: { date: string; count: number }[];
}

// Hooks
export function usePldAlerts(query: any = {}) {
  return useQuery({
    queryKey: ['pld-alerts', query],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([k, v]) => v && params.append(k, String(v)));
      const response = await api.get(`/compliance/pld/alerts?${params.toString()}`);
      return response.data;
    },
  });
}

export function usePldAlertDetail(id: string) {
  return useQuery<PldAlert>({
    queryKey: ['pld-alert', id],
    queryFn: async () => {
      const response = await api.get(`/compliance/pld/alerts/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useReviewPldAlert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, resolution, notes }: { id: string; resolution: string; notes?: string }) => {
      const response = await api.post(`/compliance/pld/alerts/${id}/review`, { resolution, notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pld-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['pld-dashboard'] });
    },
  });
}

export function usePldRules() {
  return useQuery<PldRule[]>({
    queryKey: ['pld-rules'],
    queryFn: async () => {
      const response = await api.get('/compliance/pld/rules');
      return response.data;
    },
  });
}

export function usePldRuleDetail(id: string) {
  return useQuery<PldRule>({
    queryKey: ['pld-rule', id],
    queryFn: async () => {
      const response = await api.get(`/compliance/pld/rules/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreatePldRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<PldRule>) => {
      const response = await api.post('/compliance/pld/rules', data);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pld-rules'] }),
  });
}

export function useUpdatePldRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<PldRule> & { id: string }) => {
      const response = await api.put(`/compliance/pld/rules/${id}`, data);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pld-rules'] }),
  });
}

export function useDeletePldRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/compliance/pld/rules/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pld-rules'] }),
  });
}

export function usePldConfig() {
  return useQuery<PldConfig>({
    queryKey: ['pld-config'],
    queryFn: async () => {
      const response = await api.get('/compliance/pld/config');
      return response.data;
    },
  });
}

export function useUpdatePldConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<PldConfig>) => {
      const response = await api.put('/compliance/pld/config', data);
      return response.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pld-config'] }),
  });
}

export function usePldDashboard() {
  return useQuery<PldDashboard>({
    queryKey: ['pld-dashboard'],
    queryFn: async () => {
      const response = await api.get('/compliance/pld/dashboard');
      return response.data;
    },
  });
}

// Helpers
export function getSeverityLabel(severity: string): string {
  const labels: Record<string, string> = { LOW: 'Baixa', MEDIUM: 'Média', HIGH: 'Alta', CRITICAL: 'Crítica' };
  return labels[severity] || severity;
}

export function getSeverityColor(severity: string): string {
  const colors: Record<string, string> = { LOW: 'gray', MEDIUM: 'blue', HIGH: 'orange', CRITICAL: 'red' };
  return colors[severity] || 'gray';
}

export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = { PENDING: 'Pendente', REVIEWED: 'Analisado', ESCALATED: 'Escalado', CLOSED: 'Fechado' };
  return labels[status] || status;
}

export function getResolutionLabel(resolution: string): string {
  const labels: Record<string, string> = { FALSE_POSITIVE: 'Falso Positivo', CONFIRMED: 'Confirmado', ESCALATED: 'Escalado para COAF' };
  return labels[resolution] || resolution;
}

export function getRuleTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    HIGH_VALUE: 'Valor Alto',
    FREQUENCY: 'Alta Frequência',
    SUSPICIOUS_TIME: 'Horário Suspeito',
    STRUCTURING: 'Fracionamento',
    ROUND_VALUES: 'Valores Redondos',
    GEOGRAPHIC: 'Geográfico',
    CUSTOM: 'Personalizada',
  };
  return labels[type] || type;
}
